import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import MaterialReactTable, {
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
} from "material-react-table";
import moment from "moment";
import { DocumentsQuery } from "api";

import {
  COLORS,
  EsgDatePickerField,
  EsgPrimaryLabel,
  InfoMessageBar,
  FLEX_ALIGN_CENTER_GAP_2,
  Loading,
  MarginWrapper,
  Switch,
  Pagination,
  useSwitchFilter,
  useYearMonthPeriodFilter,
} from "components";
import { useTableExpandedRows } from "components/react-table";
import { useDocuments, useGetConfigParameter, useSitesMultiselect } from "hooks";
import {
  getDateFormatmonthset,
  setToLocalStorage,
  isValidDate,
  combineDropdownOptions,
  determineAlgorithmName,
  isFeatureEnabled,
} from "utils";
import { debounce } from "lodash";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import { EsgCommonButton } from "components/inputs/esg-buttons";
import { EsgDocumentsUpload } from "components/documents-upload/esg-flow-document-upload";
import { ADD_DOCUMENT_BILL_PAGE } from "./add-document-bill";
import { UTILITY_BILL_ALL_DATAENTRIES_VIEW } from "../document-summary-v2/utility-bill/utility-bill-allDataEntries-view/consts";
import ApprovalFlowBar from "./components/approval-flow-bar";
import { dateSelectionReducer, getColumnFilterPayload } from "./helpers";
import { ACTIVE_ADDITIONAL_STATUS_DATA_ENTRIES, ACTIVE_FLOW_DATA_ENTRIES, BASE_DATA_ENTRIES, dataUploadColumns } from "./consts";
import { DataEntriesStatusType, DateObject } from "./types";
import { EsgActions } from "./components";

export const DocumentUploadPage = () => {
  // ---- Hooks
  const navigate = useNavigate();
  const currentUrl = useLocation();
  const { data: configParameter } = useGetConfigParameter();

  // ---- Const ---- 
  const currentDate = new Date();
  const firstDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  );
  const lastDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  );
  const fromDateInitial = moment(firstDay)?.format("YYYY-MM-DD");
  const toDateInitial = moment(currentDate).add(1, "day").format("YYYY-MM-DD");

  // Local Storage
  const storedFromDate = localStorage.getItem("fromDate");
  const storedToDate = localStorage.getItem("toDate");
  const rawDataEntriesStatus = localStorage.getItem("dataEntriesStatus");
  const storedDataEntriesStatus = rawDataEntriesStatus ? JSON.parse(rawDataEntriesStatus) : [];
  const rawStoredSitesFilter = localStorage.getItem("sitesFilter");
  const storedSitesFilter = rawStoredSitesFilter ? JSON.parse(rawStoredSitesFilter) : [];
  const storedSiteIdsFilter = storedSitesFilter.map((site: any) => site.value);

  const sessionFilterValue = window.sessionStorage.getItem(
    "columnFiltersDocumentUpload",
  );
  const storedfilterValues =
    (sessionFilterValue && JSON.parse(sessionFilterValue)) || [];

  const viewedDocumentID = window.sessionStorage.getItem("viewedDocumentID");
  const maxDate = dayjs().endOf('day');

  // ---- States
  const [clearDate, setClearDate] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(
    {},
  );
  const rowSelectionLength = Object.keys(rowSelection).length > 0;

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(storedfilterValues);

  const isApprovalFlowValueOn = isFeatureEnabled(configParameter, "second_layer_approval_feature");
  const isAdditionalStatusesOn = isFeatureEnabled(configParameter, "unusable_status_enabled");

  // Right below 👇 we have basicStatuses always on, that means = true;
  const dropDownStatusOption: Array<{ label: string, name: string, value: number }> = combineDropdownOptions(
    [true, isApprovalFlowValueOn, isAdditionalStatusesOn],
    [BASE_DATA_ENTRIES, ACTIVE_FLOW_DATA_ENTRIES, ACTIVE_ADDITIONAL_STATUS_DATA_ENTRIES],
  );

  const [dateError, setDateError] = useState(false);
  const fromDates = storedFromDate ? moment(storedFromDate) : fromDateInitial;
  const toDates = storedToDate ? moment(storedToDate) : toDateInitial;
  const initialState = {
    isYearly: false,
    startDate: moment(fromDates !== null ? fromDates : firstDay).format(
      "YYYY-MM-DD",
    ),
    endDate: moment(toDates !== null ? toDates : lastDay).format("YYYY-MM-DD"),
    fromDate: fromDates,
    toDate: toDates,
    view: "monthly",
  };
  const [state, dispatch] = useReducer(dateSelectionReducer, initialState);
  const { fromDate, toDate } = state;
  const [dateCurrent, setDateCurrent] = useState<DateObject>({
    datefrom: "",
    toDateValue: "",
  });
  const [dateReload, setDateReload] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [dataEntriesStatus, setDataEntriesStatus] = useState<any>([]);

  const storedValue = localStorage.getItem("togglebtn");
  const toggleBtn = storedValue ? JSON.parse(storedValue) : true;

  // ---- API's
  const { setInitialExpanded } =
    useTableExpandedRows();

  const updateExternalStates = useCallback(() => {
    setInitialExpanded();
  }, [setInitialExpanded]);

  const { period, availableYears } = useYearMonthPeriodFilter({
    updateExternalStates,
  });
  const minDate = dayjs(
    `${Math.min(...(availableYears || [dayjs().utc().year()]))}-01-01`,
  ).utc();

  const {
    siteIds,
    isSitesLoading,
    selectedSites,
    setSelectedSites,
    sitesOptions,
  } = useSitesMultiselect({
    updateExternalStates,
  });

  const handleSetSites = (param: any) => {
    setSelectedSites(param)
    localStorage.setItem(
      "sitesFilter",
      JSON.stringify(param)
    );
  }

  const handleSetDataEntriesStatus = (param: any) => {
    setDataEntriesStatus(param)
    localStorage.setItem(
      "dataEntriesStatus",
      JSON.stringify(param)
    );
  }

  const { value: isUnmoved, onSwitchChange: onIsUnmovedChange } =
    useSwitchFilter({
      key: "isUnmoved",
      updateExternalStates,
    });

  useEffect(() => {
    window.sessionStorage.setItem(
      "columnFiltersDocumentUpload",
      JSON.stringify(columnFilters),
    );
  }, [columnFilters]);

  useEffect(() => {
    if (storedSitesFilter && storedSitesFilter.length > 0) setSelectedSites(storedSitesFilter);
    if (storedDataEntriesStatus && storedDataEntriesStatus.length > 0) setDataEntriesStatus(storedDataEntriesStatus);
    const firstDate = new Date();
    const firstDateOfMonth = new Date(
      firstDate?.getFullYear(),
      firstDate?.getMonth(),
      1,
    );
    const formattedFirstDate = firstDateOfMonth;
    const datefrom = moment(formattedFirstDate).format("YYYY-MM-DD");
    const storedfromValue = localStorage.getItem("fromDate");
    const storedtoValue = localStorage.getItem("toDate");

    if (!storedfromValue) {
      localStorage.setItem(
        "fromDate",
        moment(formattedFirstDate)?.format("YYYY-MM-DD"),
      );
    }

    const lastDate = new Date();
    const currentYear = lastDate.getFullYear();
    const currentMonth = lastDate.getMonth();
    const storedYear = storedtoValue ? moment(storedtoValue).year() : null;
    const storedMonth = storedtoValue ? moment(storedtoValue).month() : null;
    const currentDates = lastDate.getDate();
    const storedDates = storedtoValue ? moment(storedtoValue).date() : null;

    const toDateValue = moment(currentDate).add(1, "day").format("YYYY-MM-DD");
    if (
      !storedtoValue ||
      (currentYear === storedYear &&
        currentMonth === storedMonth &&
        currentDates !== storedDates)
    ) {
      localStorage.setItem(
        "toDate",
        moment(currentDate).add(1, "day").format("YYYY-MM-DD"),
      );
    }

    setDateCurrent({ datefrom, toDateValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Pagination Stock 
  const getSavedPagination = () => {
    const saved = localStorage.getItem("documents-upload-pagination");
    return saved ? JSON.parse(saved) : {
      currentPage: 0,
      rowsPerPage: 50,
      totalRows: 0,
      totalPages: 0
    };
  };

  const [paginationData, setPaginationData] = useState<any>(getSavedPagination);

  const onPageChangeHandler = (newPage: any) => {
    const param = {
      ...paginationData,
      currentPage: newPage - 1
    }
    setPaginationData(param)
    localStorage.setItem("documents-upload-pagination", JSON.stringify(param));
    const paginationPayload = getColumnFilterPayload(columnFilters, { ...searchQuery, size: paginationData?.rowsPerPage, page: newPage - 1 });
    setQuery(paginationPayload)
  }

  const onPageSizeChangeHandler = (newPageSize: any) => {
    const param = {
      ...paginationData,
      rowsPerPage: newPageSize
    }
    setPaginationData(param)
    localStorage.setItem("documents-upload-pagination", JSON.stringify(param));
    const paginationPayload = getColumnFilterPayload(columnFilters, { ...searchQuery, size: newPageSize, page: paginationData?.currentPage });
    setQuery(paginationPayload)
  }

  const searchQuery = {
    uploadDateFrom: !dateError ? (fromDates !== null ? moment(fromDates).format("YYYY-MM-DD") : fromDate) : dateCurrent?.datefrom,
    uploadDateTo: !dateError ? (toDates !== null ? moment(toDates).format("YYYY-MM-DD") : toDate) : dateCurrent?.toDateValue,
    siteIds: siteIds.length > 0 ? siteIds : storedSiteIdsFilter,
    status: (dataEntriesStatus.length > 0 ? dataEntriesStatus : storedDataEntriesStatus).map((item: any) => item.label).join(","),
    isUnmoved,
    size: paginationData?.rowsPerPage,
    page: paginationData?.currentPage
  }

  const [query, setQuery] = useState<DocumentsQuery>(getColumnFilterPayload(columnFilters, searchQuery))

  const [prevQuery, setPrevQuery] = useState(query);
  const [hasChanged, setHasChanged] = useState(false);
  const [isClearButtonClicked, setIsClearButtonClicked] = useState(false);

  const { data: dataDocuments, isLoading } = useDocuments(query);

  const handleSearch = () => {
    setIsClearButtonClicked(true);
    const columnFilterPayload = getColumnFilterPayload(columnFilters, { ...searchQuery, page: 0 });
    setQuery(columnFilterPayload);
  }

  useEffect(() => {
    const isDifferent = JSON.stringify(searchQuery) !== JSON.stringify(prevQuery);
    setHasChanged(isDifferent);
    if (isDifferent) {
      setPrevQuery(query); // Update previous state if changed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const tableRows = dataDocuments?.documents || [];

  // Page Handler Functions
  const handleRowSelection = () => {
    setRowSelection({});
  };

  const handleFromDateChange = (date: any) => {
    const firstDate = new Date(date);
    const firstDateOfMonth = new Date(
      firstDate?.getFullYear(),
      firstDate?.getMonth(),
      1,
    );
    const formattedFirstDate = firstDateOfMonth;

    localStorage.setItem(
      "fromDate",
      moment(formattedFirstDate).format("YYYY-MM-DD"),
    );
    const formDateValue = moment(formattedFirstDate).format("YYYY-MM-DD");
    const fromDateExists = localStorage.getItem("fromDate");
    let formattedtest = fromDateExists !== null ? fromDateExists : fromDate;
    const toDateExists = localStorage.getItem("toDate");
    if (formDateValue === formattedtest) {
      formattedtest = toDateExists;
    }
    if (fromDate && formDateValue > formattedtest) {
      setDateError(true);
    } else {
      dispatch({
        type: "SET_FROM_DATE",
        payload: {
          value: moment(formattedFirstDate).format("YYYY-MM-DD"),
          period,
        },
      });
      setDateError(false);
    }
  };

  const handleToDateChange = (date: any) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    const selectedDate = new Date(date);
    if (Number.isNaN(selectedDate.getTime())) {
      return;
    }

    const selectedMonth = selectedDate.getMonth();
    const selectedYear = selectedDate.getFullYear();

    let toDateValue: string;

    if (currentMonth === selectedMonth && currentYear === selectedYear) {
      toDateValue = moment(today).format("YYYY-MM-DD");
    } else {
      const lastDateOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
      toDateValue = moment(lastDateOfMonth).format("YYYY-MM-DD");
    }

    localStorage.setItem("toDate", toDateValue);

    const fromDateValidation = moment(fromDates).format("YYYY-MM-DD");
    const dateValueCheck = fromDate !== null
      ? moment(fromDate).format("YYYY-MM-DD")
      : fromDateValidation;

    if (toDateValue < dateValueCheck) {
      setDateError(true);
    } else {
      dispatch({
        type: "SET_TO_DATE",
        payload: { value: toDateValue, period },
      });
      setDateError(false);
    }
  };


  useEffect(() => {
    if (dateReload === false) {
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateReload]);

  useEffect(() => {
    if (!toggleBtn) {
      const fromDateExists = localStorage.getItem("fromDate") !== null;
      const toDateExists = localStorage.getItem("toDate") !== null;
      if (fromDateExists) {
        localStorage.removeItem("fromDate");
        setDateReload(false);
      }
      if (toDateExists) {
        localStorage.removeItem("toDate");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleBtn]);

  const handleResetDate = () => {
    const firstDate = new Date();
    const firstDateOfMonth = new Date(
      firstDate?.getFullYear(),
      firstDate?.getMonth(),
      1,
    );
    const formattedFirstDate = firstDateOfMonth;
    localStorage.setItem(
      "fromDate",
      moment(formattedFirstDate)?.format("YYYY-MM-DD"),
    );

    const lastDate = new Date();
    const toDateValue = moment(lastDate)?.format("YYYY-MM-DD");
    localStorage.setItem("toDate", toDateValue);
  };

  const handleClearFilters = () => {
    dispatch({ type: "CLEAR_DATES" });
    setDateError(false);
    setClearDate(true);
    setSelectedSites([]);
    localStorage.setItem(
      "sitesFilter",
      JSON.stringify([])
    );
    localStorage.setItem(
      "dataEntriesStatus",
      JSON.stringify([])
    );

    const fromDateExists = localStorage.getItem("fromDate") !== null;
    if (fromDateExists) {
      handleResetDate();
    }
    setTimeout(() => {
      setClearDate(false);
    }, 100);
    window.location.reload();
    setIsClearButtonClicked(false)
  };

  const setDateValue = (dateValue1: any, dateValue2: any) => {
    if (dateValue1 !== null) {
      return dayjs(dateValue1);
    }
    return dateValue2;
  };

  localStorage.removeItem("setid");

  function getCircularReplacer(): (key: string, value: any) => any {
    const seen = new WeakSet();
    return (key, value) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) {
          return undefined;
        }
        seen.add(value);
      }
      return value;
    };
  }

  const debouncedHandleFromDateChange = debounce(handleFromDateChange, 500);
  const debouncedHandleToDateChange = debounce(handleToDateChange, 500);

  useEffect(() => {
    if (dataDocuments && dataDocuments?.pageResult) {
      const { page, size, totalPages, totalElements } = dataDocuments.pageResult;
      setPaginationData({
        currentPage: page,
        totalPages,
        totalRows: totalElements,
        rowsPerPage: size,
      })
    }
  }, [dataDocuments]);

  return (
    <MarginWrapper>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
        flexWrap: "wrap",
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2,
        }}>
          <Box sx={{
            maxWidth: '75%',
            "@media (max-width: 1323px)": {
              maxWidth: '60%',
            },
          }}>
            <Box sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}>
              <Stack direction="row" spacing={2}>
                <EsgDatePickerField
                  label="Start Month"
                  views={["year", "month"]}
                  openTo="month"
                  minDate={minDate}
                  maxDate={maxDate}
                  format={getDateFormatmonthset()}
                  disableFuture
                  disableHighlightToday
                  onAccept={handleFromDateChange}
                  onChange={(newValue: any) => {
                    const firstDate = new Date(newValue);
                    const firstDateOfMonth = new Date(
                      firstDate.getFullYear(),
                      firstDate.getMonth(),
                      1,
                    );
                    const formattedFirstDate = firstDateOfMonth;
                    const fromDateValue = moment(formattedFirstDate).format("YYYY-MM-DD");

                    const isDateValid = isValidDate(fromDateValue)
                    if (isDateValid) debouncedHandleFromDateChange(newValue);
                  }}
                  setDate={handleFromDateChange}
                  value={setDateValue(fromDates, fromDate)}
                  placeholdervalue="MM-YYYY"
                  filterAll={false}
                  width="140px"
                  clearDate={false}
                  disabled={!toggleBtn}
                />
                <EsgDatePickerField
                  width="140px"
                  views={["year", "month"]}
                  label="End Month"
                  minDate={minDate}
                  maxDate={maxDate}
                  openTo="month"
                  format={getDateFormatmonthset()}
                  disableHighlightToday
                  onAccept={handleToDateChange}
                  onChange={(newValue: any) => {
                    const firstDate = new Date(newValue);
                    const firstDateOfMonth = new Date(
                      firstDate.getFullYear(),
                      firstDate.getMonth(),
                      1,
                    );
                    const formattedFirstDate = firstDateOfMonth;
                    const fromDateValue = moment(formattedFirstDate).format("YYYY-MM-DD");

                    const isDateValid = isValidDate(fromDateValue)
                    if (isDateValid) debouncedHandleToDateChange(newValue);
                  }}
                  setDate={handleToDateChange}
                  value={setDateValue(toDates, toDate)}
                  clearDate={clearDate}
                  disabled={!toggleBtn}
                  placeholdervalue="MM-YYYY"
                  filterAll={false}
                />
              </Stack>

              <Box sx={{ minWidth: "210px", width: "fit-content", paddingLeft: "0px" }}>
                <EsgMultiselectControlledField
                  label="Sites"
                  disabled={isSitesLoading}
                  selectedValues={selectedSites}
                  setSelectedValues={handleSetSites}
                  options={sitesOptions}
                  isAllSelectedOnEmptySelection
                  type="rounded"
                />
              </Box>

              <Box sx={{ minWidth: "210px", width: "fit-content" }}>
                <EsgMultiselectControlledField
                  label="Data Entries Status"
                  disabled={isSitesLoading}
                  selectedValues={dataEntriesStatus}
                  setSelectedValues={handleSetDataEntriesStatus}
                  options={dropDownStatusOption}
                  isAllSelectedOnEmptySelection
                  type="rounded"
                />
              </Box>

              <Box sx={{
                paddingTop: "22px",
                maxWidth: {
                  xl: "125px",
                },
                "@media (min-width: 1600px)": {
                  maxWidth: "200px",
                }
              }}>
                <Switch
                  isChecked={isUnmoved}
                  label="Show only unmoved"
                  onChange={onIsUnmovedChange}
                />
              </Box>

              <EsgCommonButton variant={hasChanged ? "contained" : "outlined"} sx={{ width: "120px", color: hasChanged ? "white" : COLORS.Romance }} onClick={handleSearch}>{columnFilters?.length > 0 ? 'Search All' : 'Search'}</EsgCommonButton>
              <EsgCommonButton variant={isClearButtonClicked ? "contained" : "outlined"} sx={{ width: "120px", color: isClearButtonClicked ? "white" : COLORS.Romance }} onClick={handleClearFilters}>Clear Filters</EsgCommonButton>
            </Box>

            <Box sx={[{ my: 3, width: '100%' }]}>
              <Box sx={[FLEX_ALIGN_CENTER_GAP_2]}>
                <EsgDocumentsUpload isLoading={isLoading} />
                {rowSelectionLength && <EsgActions
                  rowSelection={rowSelection}
                  setrowSelection={handleRowSelection}
                />}
              </Box>
            </Box>

          </Box>
          <Box>
            <ApprovalFlowBar data={dataDocuments as DataEntriesStatusType} />
          </Box>
        </Box>
        {dateError && (
          <Box sx={[FLEX_ALIGN_CENTER_GAP_2, { mt: 1 }]}>
            <EsgPrimaryLabel sx={{ color: "red" }}>
              Invalid date selection. Please choose a month after the From Month.
            </EsgPrimaryLabel>
          </Box>
        )}
      </Box>

      {!dataDocuments ? (
        <Loading />
      ) : (
        <>
          {dataDocuments?.documents.length === 0 && (
            <InfoMessageBar>
              No bills have been uploaded for this month yet. Please check the previous month&apos;s bills or upload more bills.
            </InfoMessageBar>)}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              marginTop: "20px",
            }}
          >
            {columnFilters?.length !== 0 && (
              <InfoMessageBar>Filters are currently applied to this page only. To search the entire dataset, click <b>Search All</b></InfoMessageBar>
            )}
          </Box>
          {tableRows &&
            <>
              <MaterialReactTable
                enableTopToolbar
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                data={dataDocuments?.documents || []}
                columns={dataUploadColumns}
                enableRowSelection
                enablePagination
                manualPagination
                rowCount={paginationData.rowsPerPage}
                onRowSelectionChange={setRowSelection}
                onColumnFiltersChange={setColumnFilters}
                onGlobalFilterChange={setGlobalFilter}
                initialState={{ density: "compact" }}
                state={{ rowSelection, columnFilters, globalFilter }}
                getRowId={(row: any) => row.id}
                muiTableContainerProps={{
                  sx: {
                    "& thead tr:first-of-type th:first-of-type": {
                      paddingLeft: "20px",
                    },
                    "& tbody tr td:first-of-type": {
                      paddingLeft: "20px",
                    },
                    "& tbody tr:first-of-type td > p": {
                      display: "flex",
                      width: "100%",
                      justifyContent: "center",
                    },
                    height: "calc(100vh - 350px)",
                    border: "1px solid #D9D9D9",
                    borderRadius: "10px",
                    minWidth: "100%",
                    width: "100%",
                    maxWidth: "100%",
                    "@media (min-width: 600px)": {
                      width: "100%",
                    },
                    "@media (min-width: 960px)": {
                      width: "1100px",
                    },
                    "@media (min-width: 1280px)": {
                      width: "1100px",
                    },
                    "@media (min-width: 1440px)": {
                      width: "1100px",
                    },
                    "@media (min-width: 1680px)": {
                      width: "1300px",
                    },
                    "@media (min-width: 2400px)": {
                      width: "100%",
                    },
                  },
                }}
                muiTablePaperProps={{
                  sx: {
                    borderRadius: "10px",
                    boxShadow: "none",
                  },
                }}
                muiTopToolbarProps={{
                  sx: {
                    '& .MuiIconButton-root': {
                      display: 'none',
                    },
                    backgroundColor: "white",
                    display: Object.keys(rowSelection).length > 0 ? 'block' : 'none'
                  },
                }}
                muiBottomToolbarProps={{
                  sx: {
                    display: "none",
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    lineHeight: "30px",
                    "& .MuiBadge-root": {
                      display: "none",
                    },
                  },
                }}
                muiTableHeadRowProps={{
                  sx: {
                    "& > th": {
                      paddingTop: 1,
                      paddingBottom: 1,
                    },
                    backgroundColor: "#F8FAFC",
                  },
                }}
                muiTableBodyRowProps={(row) => ({
                  onClick: () => {
                    setToLocalStorage("dataEntries", JSON.stringify(row?.row));
                    const handleClick = async () => {
                      if (row?.row?.original?.bills !== null) {
                        if (row?.row?.original?.bills?.length > 0) {
                          window.sessionStorage.setItem(
                            "summaryDocumentID",
                            row?.row?.original?.bills[0]?.id,
                          );
                          navigate(
                            UTILITY_BILL_ALL_DATAENTRIES_VIEW.ABSOLUTE_PATH(
                              row?.row?.original?.bills[0]?.id,
                            ),
                          );
                        } else {
                          window.sessionStorage.setItem(
                            "summaryDocumentID",
                            row?.row?.original?.id as any,
                          );
                          navigate(
                            ADD_DOCUMENT_BILL_PAGE.ABSOLUTE_PATH(
                              row?.row?.original?.id as string,
                            ),
                          );
                        }
                      }

                      window.sessionStorage.setItem(
                        "viewedDocumentID",
                        row?.row?.original?.id as string,
                      );
                      setToLocalStorage("type", "Upload");
                      setToLocalStorage(
                        "Upload_URL",
                        (currentUrl?.pathname || '') + (currentUrl?.search || '')
                      );
                      setToLocalStorage(
                        "dataEntries",
                        JSON.stringify(row, getCircularReplacer()),
                      );
                      setToLocalStorage(
                        "algorithm",
                        determineAlgorithmName(row?.row?.original),
                      );
                    };
                    handleClick();
                  },
                  sx: {
                    cursor: "pointer",
                    backgroundColor:
                      String(row?.row?.original?.id) === String(viewedDocumentID)
                        ? "#DCDCDC"
                        : "",
                  },
                })}
                muiTableBodyCellProps={(row) => ({
                  sx: {
                    opacity: row.row.original.moved ||
                      row.row.original.status === "PARSED" ||
                      row.row.original.status === "NO_RESPONSE"
                      ? 1
                      : 0.5,
                    ":disabled":
                      !(
                        row.row.original.moved ||
                        row.row.original.status === "PARSED" ||
                        row.row.original.status === "NO_RESPONSE"
                      ),
                  },
                })}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "10px",
                }}
              >
                <Pagination
                  page={paginationData.currentPage + 1}
                  rowsCount={paginationData.totalRows}
                  rowsPerPage={paginationData.rowsPerPage}
                  rowsPerPageOptions={[50, 100, 200]}
                  onPageChange={onPageChangeHandler}
                  onRowsPerPageChange={onPageSizeChangeHandler}
                />
              </Box>
            </>
          }
        </>
      )}
    </MarginWrapper>
  );
};